import React, { useState, useRef } from 'react';
import {
  Box, Button, VStack, HStack, Text, Image, IconButton,
  SimpleGrid, useToast, Modal, ModalOverlay, ModalContent,
  ModalBody, ModalCloseButton, useDisclosure, Input
} from '@chakra-ui/react';
import { MdDelete, MdVisibility, MdCameraAlt, MdFlipCameraAndroid } from 'react-icons/md';

interface EvidenciaItem {
  id: string;
  file: File;
  url: string;
  type: 'file' | 'camera';
}

interface Props {
  onFilesChange: (files: File[]) => void;
  isReadOnly?: boolean;
  existingFiles?: any[];
  maxFiles?: number;
  onCameraCleanup?: () => void;
}

interface EvidenciasFieldRef {
  cleanup: () => void;
}

const EvidenciasFieldSimple = React.forwardRef<EvidenciasFieldRef, Props>(({ 
  onFilesChange, 
  isReadOnly = false, 
  existingFiles = [],
  maxFiles = 4 
}, ref) => {
  const [evidencias, setEvidencias] = useState<EvidenciaItem[]>([]);
  const [isCamera, setIsCamera] = useState(false);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [selectedImage, setSelectedImage] = useState<string>('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const startCamera = async () => {
    try {
      setIsCamera(true);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode }
      });
      
      streamRef.current = stream;
      
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current!.play();
          };
        }
      }, 100);
      
    } catch (error) {
      setIsCamera(false);
      toast({
        title: 'Error',
        description: 'No se pudo acceder a la cámara',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCamera(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !isCamera) {
      toast({ title: 'Error', description: 'Cámara no disponible', status: 'error' });
      return;
    }

    if (video.videoWidth === 0) {
      setTimeout(() => capturePhoto(), 500);
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `evidencia_${Date.now()}.jpg`, { type: 'image/jpeg' });
          const url = URL.createObjectURL(file);
          const newEvidencia: EvidenciaItem = {
            id: Date.now().toString(),
            file,
            url,
            type: 'camera'
          };
          
          const updated = [...evidencias, newEvidencia];
          setEvidencias(updated);
          onFilesChange(updated.map(e => e.file));
          
          // Apagar cámara inmediatamente después de capturar
          cleanup();
          
          toast({
            title: 'Foto capturada exitosamente',
            status: 'success',
            duration: 2000,
          });
        }
      }, 'image/jpeg', 0.9);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const totalFiles = evidencias.length + files.length;
    
    if (totalFiles > maxFiles) {
      toast({
        title: 'Límite excedido',
        description: `Máximo ${maxFiles} evidencias permitidas`,
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    const newEvidencias = files.map(file => ({
      id: Date.now().toString() + Math.random(),
      file,
      url: URL.createObjectURL(file),
      type: 'file' as const
    }));

    const updated = [...evidencias, ...newEvidencias];
    setEvidencias(updated);
    onFilesChange(updated.map(e => e.file));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeEvidencia = (id: string) => {
    const updated = evidencias.filter(e => e.id !== id);
    setEvidencias(updated);
    onFilesChange(updated.map(e => e.file));
  };

  const switchCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    if (isCamera) {
      stopCamera();
      setTimeout(startCamera, 100);
    }
  };

  const openImageModal = (url: string) => {
    setSelectedImage(url);
    onOpen();
  };

  const cleanup = () => {
    // Detener todos los tracks del stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('Track stopped:', track.kind);
      });
      streamRef.current = null;
    }
    
    // Limpiar el video element
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setIsCamera(false);
    
    // Liberar URLs de objetos
    evidencias.forEach(evidencia => {
      URL.revokeObjectURL(evidencia.url);
    });
  };

  React.useImperativeHandle(ref, () => ({
    cleanup
  }));

  return (
    <VStack spacing={4} align="stretch">
      <Text fontWeight="bold" fontSize="md">Evidencias ({evidencias.length}/{maxFiles})</Text>
      

      
      {!isReadOnly && (
        <VStack spacing={2} align="stretch">
          <HStack spacing={2}>
            <Input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleFileUpload}
              display="none"
            />
            <Button
              size="sm"
              leftIcon={<MdCameraAlt />}
              onClick={() => fileInputRef.current?.click()}
              isDisabled={evidencias.length >= maxFiles}
              bg="#5b182b"
              color="white"
              _hover={{ bg: "#691f30" }}
            >
              Subir Archivos
            </Button>
            <Button
              size="sm"
              leftIcon={<MdCameraAlt />}
              onClick={isCamera ? capturePhoto : startCamera}
              isDisabled={evidencias.length >= maxFiles}
              bg={isCamera ? "#681a2d" : "#5b182b"}
              color="white"
              _hover={{ bg: isCamera ? "#691f30" : "#681a2d" }}
            >
              {isCamera ? 'Capturar' : 'Cámara'}
            </Button>
            {isCamera && (
              <>
                <Button 
                  size="sm" 
                  leftIcon={<MdFlipCameraAndroid />}
                  onClick={switchCamera}
                  bg="#bb8c52"
                  color="white"
                  _hover={{ bg: "#a67a47" }}
                >
                  Cambiar
                </Button>
                <Button 
                  size="sm" 
                  onClick={stopCamera} 
                  variant="outline"
                  borderColor="#5b182b"
                  color="#5b182b"
                  _hover={{ bg: "#5b182b", color: "white" }}
                >
                  Cerrar
                </Button>
              </>
            )}
          </HStack>
          

        </VStack>
      )}

      {isCamera && (
        <Box 
          borderRadius="md" 
          border="2px solid" 
          borderColor="teal.300" 
          p={4}
          bg="gray.50"
          maxW="500px"
        >
          <Text fontSize="md" mb={3} textAlign="center" fontWeight="bold" color="teal.700">
            📹 Cámara {facingMode === 'user' ? 'Frontal' : 'Trasera'}
          </Text>
          <Box bg="black" borderRadius="md" p={2}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ 
                width: '100%',
                height: '300px',
                backgroundColor: '#000',
                borderRadius: '4px',
                objectFit: 'cover'
              }}
            />
          </Box>
          <Text fontSize="sm" mt={3} textAlign="center" color="teal.600">
            Presiona "Capturar" para tomar la foto
          </Text>
        </Box>
      )}

      {(evidencias.length > 0 || existingFiles.length > 0) && (
        <SimpleGrid columns={[2, 3, 4]} spacing={3}>
          {existingFiles.map((file, index) => (
            <Box key={`existing-${index}`} position="relative" borderRadius="md" overflow="hidden">
              <Image
                src={file.url?.startsWith('http') ? file.url : `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}${file.url}`}
                alt={`Evidencia ${index + 1}`}
                h="100px"
                w="100%"
                objectFit="cover"
                cursor="pointer"
                onClick={() => openImageModal(file.url?.startsWith('http') ? file.url : `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}${file.url}`)}
              />
              <IconButton
                icon={<MdVisibility />}
                size="xs"
                position="absolute"
                top={1}
                right={1}
                onClick={() => openImageModal(file.url?.startsWith('http') ? file.url : `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}${file.url}`)}
                aria-label="Ver imagen"
              />
            </Box>
          ))}
          
          {evidencias.map((evidencia) => (
            <Box key={evidencia.id} position="relative" borderRadius="md" overflow="hidden">
              <Image
                src={evidencia.url}
                alt="Evidencia"
                h="100px"
                w="100%"
                objectFit="cover"
                cursor="pointer"
                onClick={() => openImageModal(evidencia.url)}
              />
              <HStack position="absolute" top={1} right={1} spacing={1}>
                <IconButton
                  icon={<MdVisibility />}
                  size="xs"
                  onClick={() => openImageModal(evidencia.url)}
                  aria-label="Ver imagen"
                />
                {!isReadOnly && (
                  <IconButton
                    icon={<MdDelete />}
                    size="xs"
                    bg="#5b182b"
                    color="white"
                    _hover={{ bg: "#691f30" }}
                    onClick={() => removeEvidencia(evidencia.id)}
                    aria-label="Eliminar evidencia"
                  />
                )}
              </HStack>
            </Box>
          ))}
        </SimpleGrid>
      )}

      <Text fontSize="xs" color="gray.500" textAlign="center">
        Formatos: JPG, PNG, MP4 • Máximo {maxFiles} archivos
      </Text>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody p={4}>
            {selectedImage ? (
              <Image 
                src={selectedImage} 
                w="100%" 
                h="auto" 
                onError={() => console.log('Error loading image:', selectedImage)}
                onLoad={() => console.log('Image loaded successfully')}
              />
            ) : (
              <Text>No hay imagen seleccionada</Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
});

export default EvidenciasFieldSimple;